/**
 * SearchPalette — Cmd+P modal for the Obsidian Vault.
 *
 * Behavior:
 *   - Cmd / Ctrl + P opens the palette (handled by parent).
 *   - Arrow keys navigate the result list.
 *   - Enter opens the selected hit in pane A.
 *   - Shift / Cmd / Ctrl + Enter opens in pane B.
 *   - Esc closes.
 *
 * Search is driven by `useVaultSearch`, which manages the IndexedDB
 * warm cache and Fuse instance.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { testIds } from '../../lib/testIds';
import type { ParsedNote } from '../../lib/vault/parse';
import { useVaultSearch } from './useVaultSearch';

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
  clientSlug: string;
  notes: ParsedNote[];
  onOpen: (path: string, modifier: 'pane-a' | 'pane-b') => void;
}

export function SearchPalette({
  open,
  onClose,
  clientSlug,
  notes,
  onOpen,
}: SearchPaletteProps) {
  const { query, setQuery, hits, ready } = useVaultSearch(clientSlug, notes);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Fall back to alphabetical recent-style list when the query is empty.
  const displayHits = useMemo(() => {
    if (query.trim()) return hits;
    return notes
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 12)
      .map((n) => ({
        path: n.path,
        name: n.name,
        score: 0,
        matchedFields: [],
      }));
  }, [query, hits, notes]);

  // Clamp the active index whenever the list shape changes.
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(0, displayHits.length - 1)));
  }, [displayHits.length]);

  // Focus the input on open, reset the query on close.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      setQuery('');
      setActiveIdx(0);
    }
  }, [open, setQuery]);

  const commitHit = useCallback(
    (idx: number, splitMod: boolean) => {
      const hit = displayHits[idx];
      if (!hit) return;
      onOpen(hit.path, splitMod ? 'pane-b' : 'pane-a');
      onClose();
    },
    [displayHits, onClose, onOpen],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(displayHits.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const splitMod = e.metaKey || e.ctrlKey || e.shiftKey;
        commitHit(activeIdx, splitMod);
      }
    },
    [activeIdx, commitHit, displayHits.length, onClose],
  );

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          background: '#fafafa',
          border: '2px solid #000',
          boxShadow: '4px 4px 0 #00000044',
          padding: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <input
          ref={inputRef}
          data-testid={testIds.obsidianVault.searchInput}
          type="search"
          value={query}
          placeholder={
            ready
              ? 'Search notes by name, path, tag, or content…'
              : 'Indexing…'
          }
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            padding: '4px 8px',
            border: '1px solid #808080',
            background: 'white',
            fontSize: 13,
          }}
        />
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {displayHits.length === 0 ? (
            <div style={{ color: '#606060', fontStyle: 'italic', padding: 8 }}>
              {query.trim() ? 'No matches.' : 'Type to search.'}
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {displayHits.map((hit, idx) => {
                const active = idx === activeIdx;
                return (
                  <li
                    key={hit.path}
                    data-testid={testIds.obsidianVault.searchResult(hit.path)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={(e) =>
                      commitHit(idx, e.metaKey || e.ctrlKey || e.shiftKey)
                    }
                    style={{
                      padding: '4px 8px',
                      cursor: 'pointer',
                      background: active ? 'var(--xp-selection)' : 'transparent',
                      color: active ? 'white' : '#101010',
                    }}
                  >
                    <div style={{ fontWeight: 'bold' }}>{hit.name}</div>
                    <div
                      style={{
                        fontSize: 11,
                        color: active ? '#dfe6ff' : '#606060',
                      }}
                    >
                      {hit.path}
                      {hit.matchedFields.length > 0 && (
                        <span style={{ marginLeft: 6 }}>
                          · matched {hit.matchedFields.join(', ')}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div
          style={{
            fontSize: 11,
            color: '#606060',
            paddingTop: 4,
            borderTop: '1px solid #d0d0d0',
            display: 'flex',
            gap: 12,
          }}
        >
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>⌘↵ split</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
